import { getApiInstance } from '../../../helpers/api.helpers';
import { buildTimeline } from '../participant_event_timeline.mappers';
/** Paths needed to build a timeline row + decide whether a notification is configured. */
const EVENT_PATHS = [
    '_id',
    'c_start',
    'c_end',
    'type',
    'c_completed',
    'c_missed',
    'c_timezone',
    'c_hash',
    'c_task.c_name',
    'c_task_assignment.c_notifications',
    'c_task_assignment.c_order',
];
/**
 * Paths needed to render a notification's state + send/sent time and to join it to an event.
 * `c_baseline_date` identifies this occurrence's own send time; `c_date` gets advanced by the
 * scheduler to the next cycle on every reschedule (sent or not). See `mapNotif`.
 */
const NOTIF_PATHS = [
    '_id',
    'c_cortex_event',
    'c_date',
    'c_baseline_date',
    'c_status',
    'c_name',
    'c_sent',
    'c_recurring',
    'c_tz',
];
/**
 * Rows per list request, at Cortex's 1000-row max. Participants with many notifications per event
 * can accumulate thousands of c_notif rows, so the larger page minimizes round-trips; the
 * `_id`-cursor loop below still fetches every row regardless of page size.
 */
const PAGE_SIZE = 1000;
/**
 * Page through a Cortex list route by `_id` cursor until `hasMore` is false, accumulating every row.
 * Using an `_id` cursor (rather than skip/limit) keeps pagination stable and matches how the platform
 * reads generated events elsewhere. Returns the accumulated rows plus the first page so callers can
 * inspect top-level flags such as `generating`.
 */
async function fetchAllPages(api, path, paths) {
    const rows = [];
    let firstPage;
    let lastId;
    for (;;) {
        const params = {
            paths,
            sort: JSON.stringify({ _id: 1 }),
            limit: PAGE_SIZE,
        };
        if (lastId) {
            params.where = JSON.stringify({ _id: { $gt: lastId } });
        }
        const resp = await api.get(path, { params });
        const page = (resp?.data ?? {});
        firstPage = firstPage ?? page;
        const pageRows = page.data ?? [];
        rows.push(...pageRows);
        const cursorId = pageRows[pageRows.length - 1]?._id;
        if (!page.hasMore || pageRows.length === 0 || !cursorId) {
            break;
        }
        lastId = cursorId;
    }
    return { rows, firstPage: firstPage ?? {} };
}
/**
 * Phase B data layer: fetch a participant's generated events and their scheduler notification records
 * (c_notif), then join them client-side via `buildTimeline`. Returns a `generating` timeline
 * (no rows) while the backend is still materializing the schedule, mirroring
 * `fetchParticipantActivities`.
 *
 * c_notif is a top-level object readable only by its owner/administrators, so site users cannot query
 * it directly (kAccessDenied 403). Notifications are read through the site-scoped Axon route
 * `GET routes/c_sites/:siteId/c_public_users/:publicUserId/c_notifs`, which authorizes the
 * caller against the (site, participant) pair and scopes the query to the participant's account
 * (c_recipient) server-side.
 *
 * Both reads are fully paged (see `fetchAllPages`): a participant routinely has more than one page of
 * events/notifications, and reading only the first page would drop events from the timeline.
 */
export const fetchParticipantTimeline = async ({ environment, participantId, siteId, locale, }) => {
    const api = getApiInstance(environment, locale);
    const { rows: events, firstPage: firstEventsPage } = await fetchAllPages(api, `/v2/routes/c_public_users/${participantId}/c_events`, EVENT_PATHS);
    if (firstEventsPage.generating) {
        return { participantId, generating: true, events: [] };
    }
    const { rows: notifs } = await fetchAllPages(api, `/v2/routes/c_sites/${siteId}/c_public_users/${participantId}/c_notifs`, NOTIF_PATHS);
    return buildTimeline(participantId, events, notifs, false);
};
